import React, { useMemo } from 'react';
import { Store, Layers, DollarSign, Package } from 'lucide-react';

const StockByLocation = ({ data = [] }) => {
  
  // 1. Calculate Aggregate Network Metrics
  const networkMetrics = useMemo(() => {
    return data.reduce(
      (acc, item) => {
        acc.totalUniqueItems += item.ItemCount || 0;
        acc.totalQty += item.TotalQty || 0;
        acc.totalValue += item.StockValue || 0;
        return acc;
      },
      { totalUniqueItems: 0, totalQty: 0, totalValue: 0 }
    );
  }, [data]);

  // 2. Find maximum stock value to scale the visual progress bars accurately
  const maxStockValue = useMemo(() => {
    if (data.length === 0) return 1;
    return Math.max(...data.map(item => item.StockValue || 0));
  }, [data]);

  // Sort branches by stock value descending for presentation
  const sortedBranches = useMemo(() => {
    return [...data].sort((a, b) => b.StockValue - a.StockValue);
  }, [data]);

  return (
    <div className="w-full space-y-6 p-1 bg-gray-50/50 rounded-xl">
      
      {/* --- EXECUTIVE NETWORK OVERVIEW --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
            <Store className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Total Locations</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{data.length}</p>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Network Valuation</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">
              {networkMetrics.totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Total Stock Qty</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">
              {networkMetrics.totalQty.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
            <Layers className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">SKU Footprint</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">
              {networkMetrics.totalUniqueItems.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* --- SIDE-BY-SIDE ANALYTICS GRID --- */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        
        {/* Visual Share Distribution (Left Panel) */}
        <div className="lg:col-span-2 bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-800">Value Distribution</h3>
            <p className="text-xs text-gray-400 mt-0.5">Comparative footprint of capital across locations</p>
          </div>
          
          <div className="space-y-5 mt-6 mb-2">
            {sortedBranches.map((branch) => {
              const percentageOfMax = (branch.StockValue / maxStockValue) * 100;
              const percentageOfTotal = networkMetrics.totalValue > 0 
                ? (branch.StockValue / networkMetrics.totalValue) * 100 
                : 0;
                
              return (
                <div key={branch.BranchCode} className="space-y-1.5">
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-gray-700">{branch.BranchName.trim()}</span>
                    <span className="text-gray-500 font-mono">{percentageOfTotal.toFixed(1)}% of total</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500" 
                      style={{ width: `${percentageOfMax}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detailed Metrics Table (Right Panel) */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col justify-between">
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-800">Branch Stock Ledger</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-200 text-xs font-semibold uppercase text-gray-500 tracking-wider">
                  <th className="p-4">Code / Branch</th>
                  <th className="p-4 text-right">Unique SKUs</th>
                  <th className="p-4 text-right">Total Qty</th>
                  <th className="p-4 text-right">Stock Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
                {sortedBranches.map((branch) => (
                  <tr key={branch.BranchCode} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4">
                      <div className="font-semibold text-gray-900">{branch.BranchName.trim()}</div>
                      <div className="text-xs text-gray-400 font-mono mt-0.5">
                        Loc: {branch.StoreLocationCode} • {branch.BranchCode}
                      </div>
                    </td>
                    <td className="p-4 text-right font-mono text-gray-700">
                      {branch.ItemCount.toLocaleString()}
                    </td>
                    <td className="p-4 text-right font-mono text-gray-700">
                      {branch.TotalQty.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-4 text-right font-mono font-semibold text-indigo-600">
                      {branch.StockValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
        </div>

      </div>
    </div>
  );
};

export default StockByLocation;